import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { CockpitTarget, CockpitTileAnfrage, CockpitView, Unternehmen } from "@kundeninfo/api-client";
import { Hinweis, Kachelwand } from "@kundeninfo/ui/me-design";
import type { KachelwandElement } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { oeffneOderSpringe, useObjektPanel } from "../../components/objektPanel/ObjektPanelKontext.js";
import type { Route, VerlassenWaechter } from "../../router.js";
import { keepInView, topTileInView } from "../../scroll.js";
import { DUE_SOURCES } from "../termine/dueItems.js";
import { rememberDueSources } from "../termine/dueSourceMemory.js";
import { msBisMitternachtBerlin } from "../tickets/TicketBoard.js";
import { berlinerTag } from "../tickets/ticketFormat.js";
import { CockpitBar } from "./CockpitBar.js";
import { CockpitTile } from "./CockpitTile.js";
import { TilePicker } from "./TilePicker.js";
import type { CockpitClient } from "./cockpitClient.js";
import { readCompany, readCompare, readPeriod, rememberCompany, rememberCompare, rememberPeriod } from "./cockpitMemory.js";
import { drillInto, grainOf, isCurrent, toToday } from "./cockpitPeriod.js";
import type { CompareChoice, Period } from "./cockpitPeriod.js";
import { routeOfTarget } from "./cockpitTargets.js";
import { COCKPIT_TEXT_WEB as T } from "./cockpitTexts.js";
import { useCockpitLayout } from "./useCockpitLayout.js";
import { TILE_LOADING, useCockpitTiles } from "./useCockpitTiles.js";
import "./Cockpit.css";

/**
 * DER BEREICH COCKPIT (Spec „Oberfläche“). Erst der Katalog, dann die Wand: Sie steht sofort in voller Größe, jede eingeblendete Kachel
 * lädt für sich. Zeitraum, Vergleich und Unternehmen leben hier, die Kachelzustände in `useCockpitTiles`. Das Arbeitsunternehmen wirkt nicht
 * (MT R1). Die Wand kennt keine einzelne Kachel. Die Anordnung führt `useCockpitLayout`: Der Modus Anpassen speichert sofort, die Kachelwahl erst mit „Speichern“.
 */

export type CockpitProps = {
  client: CockpitClient; userId: string | undefined; companies: readonly Unternehmen[];
  phone: boolean; onNavigate: (r: Route) => void;
  /** Die Zurück-Taste fragt, solange die Kachelwahl Ungespeichertes hält. */
  registriereVerlassenWaechter?: ((frage: VerlassenWaechter) => () => void) | undefined;
};

/** „Alle“ und „aus“ fehlen in der Abfrage, die Ausrichtung steht nur nach bewusster Wahl darin (651). */
function tileQuery(period: Period, compare: CompareChoice, company: "ALL" | string): CockpitTileAnfrage {
  return { from: period.from, to: period.to,
    ...(company === "ALL" ? {} : { companyIds: [company] }),
    ...(compare.mode === "NONE" ? {} : { compare: compare.mode, ...(compare.align === null ? {} : { align: compare.align }) }) };
}

/** Wie das Board: Geht die Uhr des Geräts vor, rechnet der Server um Mitternacht noch mit gestern. */
const MIDNIGHT_MARGIN_MS = 1000;

/** Das Sprungziel einer Fälligkeitszeile (K28): `due-overdue`, `due-today`, `due-3d`, `due-7d`. */
const DUE_FILTER_PREFIX = "due-";

const dayOfClock = (): string => berlinerTag(new Date().toISOString());

function CockpitWall(p: CockpitProps & { view: CockpitView }): ReactElement {
  const { view, userId, companies, phone } = p;
  const [today, setToday] = useState(view.today);
  const [period, setPeriod] = useState(() => readPeriod(userId, view.today));
  const [compare, setCompare] = useState(() => readCompare(userId));
  // Bei nur einem Unternehmen gibt es keine Wahl, also auch keine gemerkte.
  const [company, setCompany] = useState(() => readCompany(userId, companies.length > 1 ? companies.map((c) => c.id) : []));

  const [customizing, setCustomizing] = useState(false);
  const [picking, setPicking] = useState(false);
  const wall = useRef<KachelwandElement | null>(null);
  const restore = useRef<(() => void) | null>(null);
  // Merkt die Lage im Bild: Die Wand verschiebt sich, die Ansicht rollt nach (Hausregel „Scroll bei Neuzeichnen“).
  const hold = (skipId?: string): void => {
    const tile = wall.current === null ? undefined : topTileInView(wall.current, skipId);
    restore.current = tile === undefined ? null : keepInView(tile);
  };
  const { layout, newTiles, error, arrange, reset, saveChoice } = useCockpitLayout(p.client, view, hold);
  useLayoutEffect(() => {
    restore.current?.();
    restore.current = null;
  }, [layout, error, customizing]);

  // Verschwindet der Auslöser mit seiner Wirkung (der Hinweis nach „Speichern“, der Knopf der leeren Wand), fiele der Fokus auf den `body`: Dann geht er ans Zahnrad.
  const gear = useRef<HTMLElement | null>(null);
  const focusGear = useRef(false);
  useEffect(() => {
    const active = document.activeElement;
    if (focusGear.current && (active === null || active === document.body)) gear.current?.focus();
    focusGear.current = false;
  }, [customizing, picking]);

  const definitions = useMemo(() => new Map(view.catalog.map((d) => [d.tileKey, d])), [view.catalog]);
  // Eine neue Kachel bekommt die Wand erst mit „Speichern“ in der Kachelwahl: Unter ihren Ausgeblendeten ließe sie sich einblenden, und der Server
  // nähme das zurück, solange sie „neu“ ist (648). Was der Katalog dieses Tabs nicht kennt (älterer Tab nach einem Deploy), zeichnet sie auch nicht.
  const onWall = useMemo(() => layout.order.filter((key) => definitions.has(key) && !newTiles.includes(key)), [layout.order, definitions, newTiles]);
  const visible = useMemo(() => onWall.filter((key) => !layout.hidden.includes(key)), [onWall, layout.hidden]);
  const query = useMemo(() => tileQuery(period, compare, company), [period, compare, company]);
  const { states, retry, reloadAll } = useCockpitTiles(p.client, visible, query);

  const choosePeriod = useCallback((next: Period): void => { setPeriod(next); rememberPeriod(userId, next); }, [userId]);
  const chooseCompare = (next: CompareChoice): void => { setCompare(next); rememberCompare(userId, next); };
  const chooseCompany = (next: "ALL" | string): void => { setCompany(next); rememberCompany(userId, next); };

  /**
   * Fensterfokus und Mitternacht (K31): Ein laufender Zeitraum ohne Hineinklicken rückt mit dem neuen Tag weiter. Rückt er wirklich,
   * lädt die neue Abfrage die Wand, sonst lädt sie hier neu. So läuft je Kachel genau ein Abruf.
   */
  const refresh = (): void => {
    const day = dayOfClock();
    const next = isCurrent(period, today) ? toToday(period, day) : period;
    setToday(day);
    if (next.from === period.from && next.to === period.to) reloadAll(); else setPeriod(next);
  };
  const latestRefresh = useRef(refresh);
  latestRefresh.current = refresh;
  useEffect(() => {
    const onVisibility = (): void => { if (document.visibilityState === "visible") latestRefresh.current(); };
    document.addEventListener("visibilitychange", onVisibility);
    // Der Zeitgeber stellt sich nach jedem Lauf neu, an den Umstelltagen läuft `msBisMitternachtBerlin` eine Stunde daneben.
    let timer: ReturnType<typeof setTimeout>;
    const arm = (): void => {
      timer = setTimeout(() => { latestRefresh.current(); arm(); }, msBisMitternachtBerlin(new Date()) + MIDNIGHT_MARGIN_MS);
    };
    arm();
    return () => { clearTimeout(timer); document.removeEventListener("visibilitychange", onVisibility); };
  }, []);

  const panels = useObjektPanel();
  const onTarget = (t: CockpitTarget): void => {
    // A8 (Öffnen statt Springen): Ein Ticket öffnet als Objektpanel über dem Cockpit; hat es geschrieben, lädt die Wand nach (F9).
    if (t.kind === "TICKET") {
      oeffneOderSpringe(panels, { art: "ticket", id: t.ticketId }, { onGeaendert: () => latestRefresh.current() });
      return;
    }
    // Spec „Fälligkeiten“: Die Zeile springt mit zugeschalteten Terminebene-Quellen, sonst fehlten dort die gezählten Rechnungen. Lieferantenquellen fragt die Zeitachse weiter nur mit Lieferantensicht an.
    if (t.kind === "AREA" && t.filter?.startsWith(DUE_FILTER_PREFIX) === true && userId !== undefined) rememberDueSources(userId, DUE_SOURCES);
    p.onNavigate(routeOfTarget(t));
  };
  const onDrill = (bucketStart: string): void => {
    const next = drillInto(period, bucketStart, grainOf(period));
    if (next !== null) choosePeriod(next);
  };

  return (
    <section className="ck" aria-label={T.area}>
      <CockpitBar period={period} compare={compare} company={company} companies={companies} phone={phone} today={today} customizing={customizing}
        newTiles={newTiles.length} customizeRef={gear} onPeriod={choosePeriod} onCompare={chooseCompare} onCompany={chooseCompany}
        onCustomize={() => { hold(); setCustomizing(!customizing); }} onPickTiles={() => { setPicking(true); }} />
      {error !== null && (
        <div className="ck__notice"><Hinweis art="warnung">{error === "reset" ? T.layout.resetFailed : T.layout.saveFailed}</Hinweis></div>
      )}
      {visible.length === 0 && !customizing && (
        <div className="ck__none">
          <p>{T.layout.allHidden}</p>
          <KiKnopf onClick={() => { focusGear.current = true; setCustomizing(true); }}>{T.toolbar.customize}</KiKnopf>
        </div>
      )}
      <Kachelwand ref={wall} className="ck__wand" anpassen={customizing} onAnordnungGeaendert={arrange} onZurueckgesetzt={reset}>
        {onWall.map((key) => (
          <CockpitTile key={key} definition={definitions.get(key)!} choice={layout.settings[key]!} state={states[key] ?? TILE_LOADING}
            hidden={layout.hidden.includes(key)} companies={companies} showCompany={company === "ALL" && companies.length > 1} phone={phone}
            onRetry={() => { retry(key); }} onTarget={onTarget} onDrill={onDrill} />
        ))}
      </Kachelwand>
      {picking && (
        <TilePicker catalog={view.catalog} layout={layout} newTiles={newTiles} onSave={saveChoice} registriereVerlassenWaechter={p.registriereVerlassenWaechter}
          onClose={() => { focusGear.current = true; setPicking(false); }} />
      )}
    </section>
  );
}

export function Cockpit(p: CockpitProps): ReactElement {
  const { client } = p;
  const [view, setView] = useState<CockpitView | "laedt" | "fehler">("laedt");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let current = true;
    setView("laedt");
    client.getCockpit().then(
      (loaded) => { if (current) setView(loaded); },
      () => { if (current) setView("fehler"); },
    );
    return () => { current = false; };
  }, [client, attempt]);

  if (view === "laedt") return <section className="ck" aria-label={T.area}><p className="ck__loading">{T.loading}</p></section>;
  if (view === "fehler") {
    return (
      <section className="ck" aria-label={T.area}>
        <Hinweis art="fehler" aktionen={<KiKnopf kompakt onClick={() => { setAttempt((n) => n + 1); }}>{T.reload}</KiKnopf>}>{T.catalogFailed}</Hinweis>
      </section>
    );
  }
  return <CockpitWall {...p} view={view} />;
}
